import { MedicationsService } from "./medications.service";
import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { UtilityService } from "../shared/service/utility.service";
import { AngularAppLogger } from "../angular-app-logger";
import { EMPTY } from "rxjs";
import { SpinnerService } from "../spinner/spinner.service";
import {expand, finalize, map, reduce} from 'rxjs/operators';
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: "app-medications",
    templateUrl: "./medications.component.html",
    styleUrls: ["./medications.component.scss"],
    providers: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MedicationsComponent implements OnInit {
    selectedOrg: any;
    psScope: string;
    now: any;
    startDate: Date;
    endDate: Date;
    medications: any;
    payload: any = [];
    selectedNewEntries = [];
    selectedNewResources = [];
    isAllSelected: boolean;
    isAlldisabled: boolean;
    totalSuccessCount: number = 0;
    totalFailCount: number = 0;

  constructor(
        readonly http: HttpClient,
        readonly utility: UtilityService,
        readonly appLogger: AngularAppLogger,
        readonly spinner: SpinnerService,
        readonly medicationService: MedicationsService,


        readonly router: Router,
        readonly route: ActivatedRoute,
    ) {
        const data = utility.data;
        this.selectedOrg = data.selectedOrg;
        this.psScope = this.selectedOrg.value;
    }

    ngOnInit() {
        this.now = new Date();
        this.spinner.start();
        // Get the payload from the Resolver
        this.route.data.subscribe(routeData => {
          this.payload = routeData.medicationData.payload;
          this.appLogger.logInfo(`${this.payload.length} Medication Requests fetched from EHR`, { context: `Logged from Meds component` });
        });
        this.getCibmtrMedicationRequest(this.psScope);
      //  this.spinner.end();
    }
    getCibmtrMedicationRequest(scope) {
        this.medicationService
            .getCibmtrMedicationRequests(scope)
            .pipe(
                expand(response => {
                    const next = response.link && response.link.find(l => l.relation === "next");
                    if (next) {
                        const cibmtrUrl = localStorage.getItem("cibmtr_url");
                        const queryStr = next.url.split("?")[1];
                        const modifiedUrl = `${cibmtrUrl}?${queryStr}`;
                        return this.http.get(modifiedUrl);
                    } else {
                        return EMPTY;
                    }
                }),
                map((response: any) => {
                    if (response.entry) {
                        return response.entry.flatMap((array: any) => array);
                    }
                    return [];
                }),
                reduce((acc, x) => acc.concat(x), []),
              finalize(()=> this.spinner.end())
            )
            .subscribe(
                savedEntries => {
                    if (savedEntries.length > 0 ) {
                      this.appLogger.logInfo(`${savedEntries.length} previously saved Med Reqs found`, { context: 'Retrieval of saved CIBMTR Medication Reqs', url: localStorage.getItem(`cibmtr_url`), allowDuplicate: true });
                    }
                    const entries = this.payload;
                    if (entries?.length > 0) {
                        // Filters Operationoutcome entries
                        const medicationEntries = entries.filter(function (item) {
                            return item.resource?.resourceType === "MedicationRequest";
                        });
                        for (let medicationEntry of medicationEntries) {
                            // Case I - Default state the record has not been submitted
                            if (!medicationEntry.state) {
                                medicationEntry.state = "bold";
                                medicationEntry.selected = false;
                            }

                            if (savedEntries?.length > 0) {
                                // Case II - The record has been submitted and there were no updates
                                // we cannot submit these records unless there is a change in data.
                                // sse stands for submitted  saved entry

                                const sse = savedEntries.filter(savedEntry => {
                                    const medIdentifier = savedEntry.resource.identifier[0].value.match(
                                        new RegExp("/MedicationRequest/(.*)")
                                    )[1];
                                    return (
                                        savedEntry.resource.identifier && medicationEntry.resource.id === medIdentifier
                                    );
                                });

                                if (sse.length > 0) {
                                    medicationEntry.selected = true;
                                    medicationEntry.state = "lighter";
                                    medicationEntry.resource.id = sse[0].resource.id;
                                }
                            }
                        }
                    }
                },
                error => {
                    this.appLogger.logError(error, { component: 'MedicationsComponent', context: 'getCibmtrMedicationRequests call' });
                }
            );
    }

    async submitToCibmtr() {
        this.spinner.start();
        const totalEntries = this.payload.filter(m => m.selected === true && m.state === "bold");

        if (totalEntries && totalEntries.length > 0) {
          // lowest leaf medications first
          let leafNodeBundleLookups = {};
          const leafNodeMedBundles = this.medicationService.generateLeafNodeBundles(totalEntries, this.psScope);
          if (leafNodeMedBundles && leafNodeMedBundles?.length > 0) {
            const leafNodeBundleResponses = await this.submitBundles(leafNodeMedBundles);
            leafNodeBundleLookups = this.getLocationMedicationIdLookup(leafNodeBundleResponses);
          }

          // any available non-leaf meds
          let nonLeafNodeBundleLookups = {...leafNodeBundleLookups};
          const nonLeafMeds = this.medicationService.getNonLeafNodes(totalEntries);
          let unresolvedMeds = [...nonLeafMeds];

          // Iterate through all and submit resolvable meds with medicationLookup first and then continue until all are done
          while (unresolvedMeds.length > 0) {
            const resolvableMeds = [];
            const remainingMeds = [];

            for (const med of unresolvedMeds) {
              const canResolveAllRefs = med.ingredient?.every(ing => {
                const refId = ing.itemReference?.reference?.split('/').pop();
                return refId && nonLeafNodeBundleLookups[refId];
              });

              // Resolve the reference with lookup
              if (canResolveAllRefs) {
                med.ingredient?.forEach(ing => {
                  const refId = ing.itemReference.reference.split('/').pop();
                  ing.itemReference.reference = `Medication/${nonLeafNodeBundleLookups[refId]}`;
                });
                resolvableMeds.push(med);
              } else {
                remainingMeds.push(med);
              }
            }
            if (resolvableMeds.length === 0) {
              break; // No more meds to resolve.
            }
            const bundle = this.medicationService.constructBundleEntries(resolvableMeds, this.psScope);
            bundle.entry.forEach(entry => this.medicationService.replaceReference(entry.resource));

            const response = await this.submitBundles([bundle]);
            const newLookups = this.getLocationMedicationIdLookup(response);
            nonLeafNodeBundleLookups = { ...nonLeafNodeBundleLookups, ...newLookups };

            unresolvedMeds = remainingMeds;
          }

          // Top level MedicationReference Meds
          let medicationReferenceBundleLookups = {};
          const selectedMedicationResponses = totalEntries.flatMap(entry => entry.medicationresponse ? [entry.medicationresponse] : []);
          if (selectedMedicationResponses && selectedMedicationResponses.length > 0) {
            const medicationReferenceBundles = this.medicationService.generateMedicationReferenceBundles(
              selectedMedicationResponses,
              nonLeafNodeBundleLookups,
              this.psScope
            );

            if (medicationReferenceBundles && medicationReferenceBundles?.length > 0) {
              const medicationReferenceBundleResponses = await this.submitBundles(
                medicationReferenceBundles
              );
              medicationReferenceBundleLookups = this.getLocationMedicationIdLookup(
                medicationReferenceBundleResponses
              );
            }
          }

          // MedicationRequest bundles. This will work even if there is no leafs or MedicationReferences.
          const selectedResources = totalEntries.map(entry => entry.resource);
          const medicationRequestBundles = this.medicationService.generateMedicationReqBundles(
            selectedResources,
            medicationReferenceBundleLookups,
            this.psScope
          );
          if (medicationRequestBundles && medicationRequestBundles?.length > 0) {
            const medicationRequestBundleResponses = await this.submitBundles(medicationRequestBundles);
            this.updateEntries(medicationRequestBundleResponses, totalEntries);
          }
        }
        this.spinner.end();
    }

    updateEntries(responseBundles, totalEntries) {
        responseBundles.forEach(response => {
            response.entry.forEach(entry => {
                const div = entry.response.outcome?.text?.div;
                const match = div.match(/identifier=.*\/MedicationRequest\/(.*?)&quot;/);
                if (match) {
                    const inputEntry = totalEntries.find(entry => entry.resource.id === match[1]);
                    if (inputEntry) {
                        inputEntry.selected = true;
                        inputEntry.state = "lighter";
                    }
                }
                this.totalSuccessCount = (this.totalSuccessCount ?? 0) + 1;
            });
        });
        this.totalFailCount = totalEntries?.length - this.totalSuccessCount;
        if (this.totalSuccessCount > 0 ) {
          this.appLogger.logInfo(`Successfully submitted ${this.totalSuccessCount} Med Reqs to Cibmtr`, { context: `updateEntries in Meds Component`, allowDuplicate: true });
        }
        if (this.totalFailCount > 0) {
          this.appLogger.logError(`Failed to submit ${this.totalFailCount} Med Reqs to Cibmtr`, { context: `updateEntries in Meds Component` });
        }
        this.checkForSelectAll();
    }

    getLocationMedicationIdLookup(bundleResponse: any[]) {
        let locationMedicationIdLookup = {};
        if (bundleResponse?.length > 0) {
            for (const response of bundleResponse) {
                if (response.entry) {
                    response.entry.map(entry => {
                        const location = entry.response.location;
                        const div = entry.response.outcome?.text?.div;

                        const idValue = location?.split("/")[1];

                        let match = div.match(/identifier=.*\/Medication\/(.*?)&quot;/);
                        if (match) {
                            locationMedicationIdLookup[match[1]] = idValue;
                        }
                    });
                }
            }
        }
        return locationMedicationIdLookup;
    }

    async submitBundles(bundles) {
        const responses = [];
        for (const bundle of bundles) {
            try {
                const response = await this.medicationService.postBundle(bundle);
                responses.push(response);
            } catch (error) {
                console.error(`Error posting bundle: ${error}`);
            }
        }
        return responses;
    }

    checkForSelectAll() {
        this.isAllSelected = this.payload.every(entry => entry.selected);
        this.isAlldisabled = this.payload.every(entry => entry.selected && entry.state === "lighter");
    }

    selectAll() {
        const toggleStatus = this.isAllSelected;
        this.payload.forEach(entry => {
            if (entry.state !== "lighter") {
                entry.selected = toggleStatus;
            }
        });
    }

    toggleOption() {
        this.isAllSelected = this.payload.every(function (entry) {
            return entry.selected;
        });
    }

    toggleAllOption() {
        this.isAlldisabled = this.payload.every(function (entry) {
            return entry.disabled;
        });
    }

    get toggleAlert() {
        return this.totalSuccessCount > 0 || this.totalFailCount > 0 ? "show" : "hide";
    }
}
